import { useState, useEffect, useContext } from "react";
import { ConfirmationPopup } from "./ConfirmationPopup";
import DataGridHeaderItem from "./DataGridHeaderItem";
import moment from "moment";
import { NumericFormat } from "react-number-format";
import { TextBox } from "./TextBox";
import { TextItem } from "./TextItem";
import { apiDownloadFile } from "../../services/FilesService";
import { Picture } from "./Picture";
import AddButtonInTopBar from "../AddButtonInTopBar";
import Button from "./Button";
import { useTranslation } from "react-i18next";
import TranslationContext from "../../providers/TranslationContext";

interface Props {
  rows: any[];
  cols: any[];
  loading: boolean;
  showEditButton?: boolean;
  showDeleteButton?: boolean;
  handleEditAction?: any;
  handleDeleteAction?: any;
  showCustomButton?: boolean;
  handleCustomAction?: any;
  customButtonText?: string;
  onDeleteRow?: any;
  // Potvrzeni si resi stranka sama - typicky kdyz potrebuje do hlasky doplnit data ze serveru.
  skipDeleteConfirmation?: boolean;
  setPictureUploadPopupData?: any;
  additionalClassName?: string;
  // Pokud je zadán, resetuje se stránkování až při jeho změně (ne při každé změně dat).
  resetKey?: any;
}

require("./DataGrid.css");

const DataGrid = (props: Props) => {
  const { t } = useTranslation();
  const PAGE_SIZE = 200;
  const rowRenderOptimizationStyle: any = {
    contentVisibility: "auto",
    containIntrinsicSize: "40px",
  };
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);

  const onDeleteButtonClick = () => {
    setDeleteConfirmationVisible(true);
  };

  const handleDeleteButtonClick = (row: any) => {
    if (props.skipDeleteConfirmation) {
      if (props.onDeleteRow) {
        props.onDeleteRow(row);
      }
      return;
    }

    setActiveRow(row);
    setDeleteConfirmationVisible(true);
  };

  const [rows, setRows] = useState(props.rows);
  const [cols, setCols] = useState(props.cols);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortCol, setSortCol] = useState(null);
  const [multiSelectFilters, setMultiSelectFilters] = useState(Array());
  const [textFilters, setTextFilters] = useState(Array());
  const [activeRow, setActiveRow] = useState(null);

  useEffect(() => {

    const newCols = props.cols;
    newCols.forEach((c) => {
      if (c.filterable && c.filterType === "dropdown") {
        const arrStrings = props.rows
          .map((p: any) => p[c.dataField])
          .filter((value, index, self) => {
            return self.indexOf(value) === index && value;
          });
        const arr = arrStrings.map((a, i) => {
          return {
            name: a,
            id: i,
            selected: false,
          };
        });
        c.filters = arr;
      }
    });
    setCols(newCols);
    let newRows = props.rows;
    if (sortCol) {
      newRows = doSort(sortCol, newRows);
    }
    if (textFilters) {
      newRows = filterRowsByTextFilters(textFilters, newRows);
      if (multiSelectFilters.length) {
        newRows = filterByMultiSelectFilters(multiSelectFilters, newRows);
      }
    }
    setRows(newRows);
  }, [props.rows, props.cols]);

  const handleSort = (col: any) => {
    let newRows = rows;
    newRows = doSort(col, newRows);
    setRows([...newRows]);
    setSortCol(col);
  };
  const doSort = (col: any, rows: any) => {
    const newRows = rows.sort((a: any, b: any) => {
      const aIVal = a[col.dataField] ? a[col.dataField] : "";
      const bIVal = b[col.dataField] ? b[col.dataField] : "";
      const aVal =
        !isNaN(parseFloat(aIVal)) && isFinite(aIVal)
          ? aIVal
          : aIVal.toLowerCase();
      const bVal =
        !isNaN(parseFloat(bIVal)) && isFinite(bIVal)
          ? bIVal
          : bIVal.toLowerCase();
      if (aVal < bVal) {
        return col.direction ? -1 : 1;
      }
      if (aVal > bVal) {
        return col.direction ? 1 : -1;
      }
      return 0;
    });
    return newRows;
  };

  const cancelFilter = () => {
    setRows(props.rows);
    setCurrentPage(0);
  };

  const handleTextFilter = (filter: any) => {
    if (filter) {
      let newTextFilters = textFilters;
      if (!filter.filterBy) {
        const index = newTextFilters.findIndex(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (index > -1) {
          textFilters.splice(index, 1);
        }
      } else {
        const newFilter = newTextFilters.find(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (newFilter) {
          newFilter.filterBy = filter.filterBy;
        } else {
          newTextFilters.push(filter);
        }
      }
      setTextFilters(newTextFilters);
      let newRows = filterRowsByTextFilters(newTextFilters, props.rows);
      if (multiSelectFilters.length) {
        newRows = filterByMultiSelectFilters(multiSelectFilters, newRows);
      }
      if (sortCol) {
        newRows = doSort(sortCol, newRows);
      }
      setRows([...newRows]);
      setCurrentPage(0);
    }
  };

  const filterRowsByTextFilters = (filters: any, rows: any) => {
    let newRows = rows;
    if (filters && filters.length) {
      filters.forEach((f) => {
        newRows = newRows.filter((r) => {
          if (r && r[f.col.dataField] !== undefined && r[f.col.dataField] !== null) {
            let valueAsString = r[f.col.dataField].toString();
            return valueAsString.toLowerCase().indexOf(f.filterBy.toLowerCase()) > -1;
          }

          return false;
        });
      });
    }
    return newRows;
  };

  const handleMultiSelectFilterChange = (filter: any) => {
    if (filter) {
      let newMultiSelectFilters = multiSelectFilters;
      if (!filter.selectedFilters.length) {
        const index = newMultiSelectFilters.findIndex(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (index > -1) {
          newMultiSelectFilters.splice(index, 1);
        }
      } else {
        const newFilter = newMultiSelectFilters.find(
          (f) => f.col.dataField === filter.col.dataField
        );
        if (newFilter) {
          newFilter.selectedFilters = filter.selectedFilters;
        } else {
          newMultiSelectFilters.push(filter);
        }
      }
      setMultiSelectFilters(newMultiSelectFilters);
      let newRows = filterByMultiSelectFilters(
        newMultiSelectFilters,
        props.rows
      );
      if (textFilters.length) {
        newRows = filterRowsByTextFilters(textFilters, newRows);
      }
      if (sortCol) {
        newRows = doSort(sortCol, newRows);
      }
      setRows([...newRows]);
      setCurrentPage(0);
    }
  };

  const filterByMultiSelectFilters = (filters: any, rows: any) => {
    let newRows = rows;
    if (filters && filters.length) {
      filters.forEach((f) => {
        let orRows = Array();
        f.selectedFilters.forEach((s) => {
          let tempRows = newRows.filter((r) => {
            return r[f.col.dataField] === s.name;
          });
          tempRows.forEach((r) => {
            orRows.push(r);
          });
        });
        newRows = orRows;
      });
    }
    return newRows;
  };

  const [width, setWidth] = useState(window.innerWidth);
  const breakpoint = 620;

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Když se změní počet řádků (např. po filtrování), vrátíme se na první stránku.
  useEffect(() => {
    if (props.resetKey === undefined) {
      setCurrentPage(0);
    }
  }, [rows.length]);

  useEffect(() => {
    if (props.resetKey !== undefined) {
      setCurrentPage(0);
    }
  }, [props.resetKey]);

  if (props.loading) {
    return <span>Načítám ...</span>;
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, rows.length);
  const pagedRows = rows.slice(pageStart, pageEnd);
  const showPagination = rows.length > PAGE_SIZE;

  const renderPagination = () => {
    if (!showPagination) {
      return null;
    }

    const windowSize = 7;
    let startPage = Math.max(0, safePage - Math.floor(windowSize / 2));
    let endPage = Math.min(totalPages, startPage + windowSize);
    startPage = Math.max(0, endPage - windowSize);

    const navButtonStyle: any = {
      minWidth: "44px",
      fontSize: "16px",
      lineHeight: "1.2",
      fontWeight: 700,
      padding: "4px 10px",
    };

    const pageButtons: JSX.Element[] = [];
    for (let p = startPage; p < endPage; p++) {
      const rangeStart = p * PAGE_SIZE + 1;
      const rangeEnd = Math.min((p + 1) * PAGE_SIZE, rows.length);
      pageButtons.push(
        <button
          key={p}
          type="button"
          onClick={() => setCurrentPage(p)}
          className={`btn ${p === safePage ? "btn-primary" : "btn-outline-primary"}`}
          style={{ minWidth: "100px", fontSize: "15px", padding: "4px 10px" }}
        >
          {rangeStart}-{rangeEnd}
        </button>
      );
    }

    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          alignItems: "center",
          margin: "8px 0",
        }}
      >
        <button
          type="button"
          onClick={() => setCurrentPage(0)}
          disabled={safePage === 0}
          className="btn btn-outline-secondary"
          style={navButtonStyle}
          title={t('General.operation')}
        >
          &laquo;
        </button>
        <button
          type="button"
          onClick={() => setCurrentPage(Math.max(0, safePage - 1))}
          disabled={safePage === 0}
          className="btn btn-outline-secondary"
          style={navButtonStyle}
        >
          &lsaquo;
        </button>
        {pageButtons}
        <button
          type="button"
          onClick={() => setCurrentPage(Math.min(totalPages - 1, safePage + 1))}
          disabled={safePage >= totalPages - 1}
          className="btn btn-outline-secondary"
          style={navButtonStyle}
        >
          &rsaquo;
        </button>
        <button
          type="button"
          onClick={() => setCurrentPage(totalPages - 1)}
          disabled={safePage >= totalPages - 1}
          className="btn btn-outline-secondary"
          style={navButtonStyle}
        >
          &raquo;
        </button>
        <span style={{ marginLeft: "8px", color: "#555", fontSize: "15px" }}>
          {pageStart + 1}-{pageEnd} / {rows.length}
        </span>
      </div>
    );
  };

  if (width < breakpoint)
    return (
      <div>
        {deleteConfirmationVisible && (
          <ConfirmationPopup
            key="ConfirmationPopup"
            handleConfirmation={() => {
              if (activeRow && props.onDeleteRow) {
                props.onDeleteRow(activeRow);
              }
              setDeleteConfirmationVisible(false);
              setActiveRow(null);
            }}
            handleClose={() => {
              setDeleteConfirmationVisible(false);
            }}
            header={t('General.GridOperations.DeleteConfirmationHeader')}
            text={t('General.GridOperations.DeleteConfirmationMessage')}
          />
        )}
        {renderPagination()}
        {pagedRows.map((row) => {
          return (
            <div className="mb-3 card">
              <div className="card-header">
                {(props.showEditButton || props.showDeleteButton) && (
                  <div className="text-right">
                    {props.showEditButton && (
                      <i
                        onClick={() => {
                          props.handleEditAction(row.id);
                        }}
                        className="fas fa-pencil-alt action-button"
                        style={{ marginRight: "10px", color: "green" }}
                      ></i>
                    )}
                    {props.showDeleteButton && (
                      <i
                        className="fas fa-trash action-button"
                        style={{ color: "purple" }}
                        onClick={() => {
                          handleDeleteButtonClick(row);
                        }}
                      ></i>
                    )}
                  </div>
                )}
              </div>
              <div className="card-body">
                {cols.map((col) => {
                  return (
                    <TextItem label={col.name} value={row[col.dataField]} />
                  );
                })}
              </div>
            </div>
          );
        })}
        {renderPagination()}
      </div>
    );
  else
    return (
      <div
        className={`${props.additionalClassName
          ? `gridTable ${props.additionalClassName}`
          : `gridTable`
          }`}
      >
        {deleteConfirmationVisible && (
          <ConfirmationPopup
            key="ConfirmationPopup"
            handleConfirmation={() => {
              if (activeRow && props.onDeleteRow) {
                props.onDeleteRow(activeRow);
              }
              setDeleteConfirmationVisible(false);
              setActiveRow(null);
            }}
            handleClose={() => {
              setDeleteConfirmationVisible(false);
            }}
            header={t('General.DeletionPopup.Confirmation')}
            text={t('General.DeletionPopup.Message')}
          />
        )}
        {renderPagination()}
        <table className="table table-striped table-hover table-bordered table-sm tableStickyHeader">
          <thead>
            <tr>
              {cols.map((col) => {
                //return <th scope="col">{col.name}</th>;
                return (
                  <DataGridHeaderItem
                    col={col}
                    onSort={handleSort}
                    currSortCol={sortCol}
                    onTextFilter={handleTextFilter}
                    onMultiSelectFilterChange={handleMultiSelectFilterChange}
                    onCancelFilter={cancelFilter}
                  />
                );
              })}

              {(props.showDeleteButton || props.showEditButton) && (
                <th scope="col">{t('General.operation')}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row: any, rowIndex: number) => {
              return (
                <tr key={row.id ?? rowIndex} style={rowRenderOptimizationStyle}>
                  {props.cols.map((col: any) => {
                    if (col.isLinkToItem) {
                      return (
                        <td key={col.dataField} className={`${"dataFieldCell"}_${col.dataField}`}>
                          <a href="#">{row[col.dataField]}</a>
                        </td>
                      );
                    } else if (col.fieldType === "datetime") {
                      let val = row[col.dataField];
                      let formattedValue;
                      if (val) {
                        formattedValue = moment(val).format(
                          "DD.MM.YYYY"
                        );
                      }
                      return <td key={col.dataField}>{formattedValue}</td>;
                    } else if (col.fieldType === "number" || col.fieldType === "money") {
                      return (
                        <td key={col.dataField} className={`${"dataFieldCell"}_${col.dataField}`}>
                          <NumericFormat
                            value={row[col.dataField]}
                            decimalScale={2}
                            displayType={"text"}
                            decimalSeparator={','}
                          />
                        </td>
                      );

                    } else if (col.fieldType === 'numberWithGreenForPositiveAndRedForNegative') {

                      let backgroundColor = row[col.dataField] <= 0 ? '#fbc9c9' : '#dcffdc';
                      if (row[col.dataField] == 0) {
                        backgroundColor = 'transparent';
                      }

                      return (
                        <td key={col.dataField} style={{ backgroundColor: backgroundColor }} className={`${"dataFieldCell"}_${col.dataField}`}>
                          <NumericFormat
                            value={row[col.dataField]}
                            decimalScale={2}
                            displayType={"text"}
                            decimalSeparator={','}
                          />
                        </td>
                      );
                    }
                    else if (col.fieldType === "boolean") {
                      return (
                        <td key={col.dataField} className={`${"dataFieldCell"}_${col.dataField}`}>
                          {!!row[col.dataField] ? t('General.Values.True') : t('General.Values.False')}
                        </td>
                      );
                    }
                    else if (col.fieldType === "multi") {
                      let value: string[] = [];
                      let options = row[col.dataField];
                      if (options) {
                        options.forEach((o: any) => {
                          value.push(o[col.displayField]);
                        });
                      }
                      return (<td key={col.dataField} className={`${"dataFieldCell"}_${col.dataField}`}>{value.join(", ")}</td>)
                    } else if (col.dataField === "colorName") {
                      var isPictureField = row["isPicture"];
                      if (!isPictureField) {
                        return (
                          <td key={col.dataField} style={{ textAlign: "center" }}>
                            <a href="#" style={{ display: "inline-flex" }}>
                              <div
                                className="colorColumn"
                                style={{
                                  margin: "auto",
                                  width: col.colorWidth ?? "100px",
                                  height: "32px",
                                  backgroundColor: row["rgbColor"],
                                }}
                              >
                                <div className="colorTextValue">
                                  {row[col.dataField]}
                                </div>
                              </div>
                              {props.showCustomButton && (
                                <input
                                  type="button"
                                  value={props.customButtonText}
                                  onClick={() => {
                                    props.handleCustomAction(row.id);
                                  }}
                                  className="btn btn-sm btn-primary"
                                />
                              )}
                            </a>
                          </td>
                        );
                      } else {
                        let pictureName =
                          row["pictureName"] ??
                          `odstin_${row.name}_${row.id}.jpg`;
                        return (
                          <td key={col.dataField} style={{ textAlign: "center" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                              <Picture fileName={pictureName} />
                              {props.setPictureUploadPopupData && (
                                <Button
                                  onClick={() => {
                                    var fileName = pictureName;
                                    props.setPictureUploadPopupData(
                                      row["id"],
                                      fileName
                                    );
                                  }}
                                  text={t('General.GridOperations.UploadImage')}
                                  id={
                                    row["pictureName"] ??
                                    `odstin_${row["name"]}_${row["id"]}.jpg`
                                  }
                                />
                              )}
                            </div>
                          </td>
                        );
                      }
                    } else {
                      return (
                        <td key={col.dataField} className={`${"dataFieldCell"}_${col.dataField}`}>
                          {row[col.dataField] ? row[col.dataField].toString() : ''}
                        </td>
                      );
                    }
                  })}

                  {(props.showEditButton || props.showDeleteButton) && (
                    <td key="operations" className="text-center">
                      {props.showEditButton && (
                        <i
                          onClick={() => {
                            props.handleEditAction(row.id);
                          }}
                          className="fas fa-pencil-alt action-button"
                          style={{ marginRight: "10px", color: "green" }}
                        ></i>
                      )}
                      {props.showDeleteButton && (
                        <i
                          className="fas fa-trash action-button"
                          style={{ color: "purple" }}
                          onClick={() => {
                            handleDeleteButtonClick(row);
                          }}
                        ></i>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {renderPagination()}
      </div>
    );
};

export default DataGrid;
